// Shrink a pasted picture before it is uploaded and mailed.
//
// A retina screenshot is routinely 3-6 MB of PNG; nobody reading an email needs 2880 px of it. Downscaling to
// <= 1800 px on the longest side and re-encoding turns that into a few hundred KB, which uploads in a blink and
// keeps the client's inbox light. Small images are left exactly as they are, transparency is preserved (PNG),
// and ANY failure (no canvas, undecodable file, a browser quirk) returns the original untouched - compression
// is an optimisation, never a reason a paste can fail.
//
// The decision rules are pure and unit-tested; the canvas work is the thin browser shell around them.

export const IMAGE_MAX_SIDE = 1800;
/** At or under this a picture is uploaded as it is (no decode, no re-encode). */
export const IMAGE_SKIP_BELOW_BYTES = 250 * 1024;
export const IMAGE_JPEG_QUALITY = 0.86;
/** A lossless PNG up to this size stays PNG (UI screenshots with text stay crisp); a bigger one becomes JPEG. */
export const IMAGE_PNG_KEEP_BYTES = 450 * 1024;
/** Safety net: decoding that has not finished by now is abandoned and the original is used. */
const DECODE_TIMEOUT_MS = 8000;

const COMPRESSIBLE = new Set(['image/png', 'image/jpeg', 'image/jpg', 'image/webp']);

/** Largest size that fits `maxSide` on its longest edge, keeping the aspect ratio and never upscaling. */
export function fitWithin(width: number, height: number, maxSide: number = IMAGE_MAX_SIDE): { width: number; height: number; scaled: boolean } {
  const w = Math.max(1, Math.round(Number(width) || 0));
  const h = Math.max(1, Math.round(Number(height) || 0));
  const longest = Math.max(w, h);
  if (longest <= maxSide) return { width: w, height: h, scaled: false };
  const k = maxSide / longest;
  return { width: Math.max(1, Math.round(w * k)), height: Math.max(1, Math.round(h * k)), scaled: true };
}

/** Which format to emit: JPEG sources stay JPEG; transparency forces PNG; otherwise PNG while it is small. */
export function pickOutputFormat(args: { sourceType: string; hasAlpha: boolean; pngBytes: number }): 'png' | 'jpeg' {
  const t = String(args.sourceType || '').toLowerCase();
  if (t === 'image/jpeg' || t === 'image/jpg') return 'jpeg';
  if (args.hasAlpha) return 'png';
  return args.pngBytes <= IMAGE_PNG_KEEP_BYTES ? 'png' : 'jpeg';
}

/** The file name with the extension that matches what was actually emitted ("shot.PNG" + jpeg -> "shot.jpg"). */
export function renameForFormat(name: string, format: 'png' | 'jpeg'): string {
  const base = String(name || 'image').replace(/\.[A-Za-z0-9]{1,5}$/, '') || 'image';
  return `${base}.${format === 'jpeg' ? 'jpg' : 'png'}`;
}

/** True when a picture of this type is worth decoding at all (GIF may be animated; SVG/HEIC are left alone). */
export function isCompressible(type: string): boolean {
  return COMPRESSIBLE.has(String(type || '').toLowerCase());
}

// -- Browser shell -------------------------------------------------------------------------------

interface Decoded {
  source: CanvasImageSource;
  width: number;
  height: number;
  close: () => void;
}

async function decode(blob: Blob): Promise<Decoded | null> {
  if (typeof createImageBitmap === 'function') {
    const bmp = await createImageBitmap(blob);
    return { source: bmp, width: bmp.width, height: bmp.height, close: () => { try { bmp.close(); } catch { /* ignore */ } } };
  }
  if (typeof Image === 'undefined' || typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') return null;
  return new Promise<Decoded | null>((resolve) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    const done = (v: Decoded | null) => { try { URL.revokeObjectURL(url); } catch { /* ignore */ } resolve(v); };
    const timer = setTimeout(() => done(null), DECODE_TIMEOUT_MS);
    img.onload = () => { clearTimeout(timer); done({ source: img, width: img.naturalWidth, height: img.naturalHeight, close: () => undefined }); };
    img.onerror = () => { clearTimeout(timer); done(null); };
    img.src = url;
  });
}

// The few canvas members used, written out here so this compiles whatever DOM typings the toolchain ships
// (older TypeScript libs have no OffscreenCanvas) and works with either kind of canvas at runtime.
interface Ctx2D {
  drawImage: (...args: any[]) => void; // eslint-disable-line @typescript-eslint/no-explicit-any
  getImageData: (x: number, y: number, w: number, h: number) => { data: ArrayLike<number> };
}
interface AnyCanvas {
  getContext: (kind: '2d') => Ctx2D | null;
  convertToBlob?: (opts: { type: string; quality?: number }) => Promise<Blob>;
  toBlob?: (cb: (b: Blob | null) => void, type?: string, quality?: number) => void;
}

function makeCanvas(width: number, height: number): AnyCanvas | null {
  const Off = (globalThis as any).OffscreenCanvas; // eslint-disable-line @typescript-eslint/no-explicit-any
  if (typeof Off === 'function') return new Off(width, height) as AnyCanvas;
  if (typeof document !== 'undefined') {
    const c = document.createElement('canvas');
    c.width = width;
    c.height = height;
    return c as unknown as AnyCanvas;
  }
  return null;
}

function canvasToBlob(canvas: AnyCanvas, type: string, quality?: number): Promise<Blob | null> {
  if (typeof canvas.convertToBlob === 'function') return canvas.convertToBlob({ type, quality });
  return new Promise((resolve) => {
    if (typeof canvas.toBlob === 'function') canvas.toBlob((b) => resolve(b), type, quality);
    else resolve(null);
  });
}

/** True when any pixel is not fully opaque (a full scan: a single transparent corner must not become black). */
function hasTransparency(ctx: Ctx2D, width: number, height: number): boolean {
  const data = ctx.getImageData(0, 0, width, height).data;
  for (let i = 3; i < data.length; i += 4) if (data[i] < 255) return true;
  return false;
}

export interface CompressedImage {
  blob: Blob;
  name: string;
  /** false = the original is returned as it came. */
  changed: boolean;
}

/**
 * Downscale and re-encode a pasted picture. Never rejects: on any problem the ORIGINAL comes back
 * (`changed:false`), so the caller can always carry on.
 */
export async function compressImageForEmail(file: Blob, name: string = 'image.png'): Promise<CompressedImage> {
  const original: CompressedImage = { blob: file, name, changed: false };
  let decoded: Decoded | null = null;
  try {
    const type = String(file.type || '').toLowerCase();
    if (!isCompressible(type) || file.size <= IMAGE_SKIP_BELOW_BYTES) return original;

    decoded = await decode(file);
    if (!decoded || !(decoded.width > 0) || !(decoded.height > 0)) return original;

    const { width, height, scaled } = fitWithin(decoded.width, decoded.height);
    const canvas = makeCanvas(width, height);
    const ctx = canvas ? canvas.getContext('2d') : null;
    if (!canvas || !ctx) return original;
    ctx.drawImage(decoded.source, 0, 0, width, height);

    const isJpeg = type === 'image/jpeg' || type === 'image/jpg';
    const alpha = isJpeg ? false : hasTransparency(ctx, width, height);
    let out: Blob | null = null;
    let format: 'png' | 'jpeg';
    if (isJpeg) {
      format = 'jpeg';
      out = await canvasToBlob(canvas, 'image/jpeg', IMAGE_JPEG_QUALITY);
    } else {
      const png = await canvasToBlob(canvas, 'image/png');
      if (!png) return original;
      format = pickOutputFormat({ sourceType: type, hasAlpha: alpha, pngBytes: png.size });
      out = format === 'png' ? png : await canvasToBlob(canvas, 'image/jpeg', IMAGE_JPEG_QUALITY);
    }
    if (!out || out.size <= 0) return original;
    // Only use the result when it actually helped: smaller, or at least brought down to size.
    if (!scaled && out.size >= file.size) return original;
    return { blob: out, name: renameForFormat(name, format), changed: true };
  } catch {
    return original;
  } finally {
    try { decoded?.close(); } catch { /* ignore */ }
  }
}
